import { useEffect, useState, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import axios from 'axios';
import DeleteModal from '../../components/modals/DeleteModal';
import SuccessModal from '../../components/modals/SuccessModal';
import '../../styles/ManageMarketplace.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ManageBusinessCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [deleteModalInfo, setDeleteModalInfo] = useState({ id: null, confirmMessage: '', type: 'primary' });
    const [errorMsg, setErrorMsg] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalMessage, setSuccessModalMessage] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);


         const fetchCategories = useCallback(async () => {
                setLoading(true);
                try {
                    const response = await axios.get(`${BASE_URL}/api/admin/categories/get-primary-categories`);
                    if (response.data.success) {
                        // Map backend fields to frontend id/name format
                        const mapped = response.data.categories.map(cat => ({
                            id: cat.primary_category_id,
                            name: cat.primary_category_name,
                            created_at: cat.created_at
                        }));
                        setCategories(mapped);
                    }
                } catch (error) {
                    console.error('Error fetching categories:', error);
                    setErrorMsg('Failed to load categories. Please try again.');
                } finally {
                    setLoading(false);
                }
            }, []);
        
            useEffect(() => {
              fetchCategories();
            }, [
              fetchCategories,
            ]);

            // Filter categories based on search term
            const filteredCategories = categories.filter(cat =>
                cat.name.toLowerCase().includes(searchTerm.toLowerCase())
            );

            const getAuthHeaders = () => {
                const token = localStorage.getItem('authToken');
                return token ? { Authorization: `Bearer ${token}` } : {};
            };

            const handleOpenAddModal = () => {
                setEditingCategory(null);
                setFormData({ name: '' });
                setErrorMsg('');
                setShowAddModal(true);
            };

            const handleOpenEditModal = (category) => {
                setEditingCategory(category);
                setFormData({ name: category.name });
                setErrorMsg('');
                setShowAddModal(true);
            };

            const handleCloseModal = () => {
                setShowAddModal(false);
                setEditingCategory(null);
                setFormData({ name: '' });
                setErrorMsg('');
            };

            const handleDeleteClick = (category) => {
                setDeleteModalInfo({
                    id: category.id,
                    confirmMessage: `Are you sure you want to delete "${category.name}"?`,
                    name: category.name
                });
                setShowDeleteModal(true);
            };

            const handleDeleteSuccess = () => {
                setSuccessModalMessage(`Category "${deleteModalInfo.name}" deleted successfully!`);
                setShowSuccessModal(true);
                setShowDeleteModal(false);
                setDeleteModalInfo({ id: null, confirmMessage: '', name: '' });
                fetchCategories();
            };

            const handleDeleteError = (error) => {
                setErrorMsg(error?.message || 'Failed to delete category. Please try again.');
            };

            const handleAddOrEditCategory = async () => {
                if (!formData.name.trim()) {
                    setErrorMsg('Category name is required');
                    return;
                }

                setSubmitLoading(true);
                try {
                    const authHeaders = getAuthHeaders();
                    let response;

                    if (editingCategory) {
                        // Edit existing category
                        response = await axios.put(
                            `${BASE_URL}/api/admin/categories/update-primary-category/${editingCategory.id}`,
                            { primary_category_name: formData.name },
                            { headers: authHeaders }
                        );
                    } else {
                        // Create new category
                        response = await axios.post(
                          `${BASE_URL}/api/admin/categories/add-primary-category`,
                          { primary_category_name: formData.name },
                          { headers: authHeaders },
                        );
                    }

                    if (response.data.success) {
                        setSuccessModalMessage(
                            editingCategory
                                ? `Category "${formData.name}" updated successfully!`
                                : `Category "${formData.name}" created successfully!`
                        );
                        setShowSuccessModal(true);
                        handleCloseModal();
                        fetchCategories();
                    } else {
                        setErrorMsg(response.data.message || 'Failed to save category');
                    }
                } catch (error) {
                    console.error('Error saving category:', error);
                    setErrorMsg(error.response?.data?.message || 'Failed to save category. Please try again.');
                } finally {
                    setSubmitLoading(false);
                }
            };

            const handleDeleteCategory = async () => {
                setSubmitLoading(true);
                try {
                    const authHeaders = getAuthHeaders();
                    const response = await axios.delete(
                      `${BASE_URL}/api/admin/categories/delete-primary-category/${deleteModalInfo.id}`,
                      { headers: authHeaders },
                    );

                    if (response.data.success) {
                        setSuccessModalMessage(`Category "${deleteModalInfo.name}" deleted successfully!`);
                        setShowSuccessModal(true);
                        setShowDeleteModal(false);
                        setDeleteModalInfo({ id: null, confirmMessage: '', name: '' });
                        fetchCategories();
                    } else {
                        setErrorMsg(response.data.message || 'Failed to delete category');
                    }
                } catch (error) {
                    console.error('Error deleting category:', error);
                    setErrorMsg(error.response?.data?.message || 'Failed to delete category. Please try again.');
                } finally {
                    setSubmitLoading(false);
                }
            };


  return (
    <div className="marketplace-container mt-4">
      <div className="marketplace-header">
        <h1>Manage Business Categories</h1>
      </div>
      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        message={successModalMessage}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        id={deleteModalInfo.id}
        deleteUrl={`/api/admin/categories/delete-primary-category/:id`}
        confirmMessage={deleteModalInfo.confirmMessage}
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteError={handleDeleteError}
        requestOptions={{
          headers: getAuthHeaders()
        }}
      />
      {errorMsg && (
        <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      )}

      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage Business Categories</h2>
          <button
            className="btn-custom-primary add-marketplace-btn"
            onClick={handleOpenAddModal}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add Business Category
          </button>
        </div>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading categories...</p>
          </div>
        ) : (
          <Table className="align-middle marketplace-table" hover>
            <thead>
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th>
                  <div className="d-flex flex-column gap-2">
                    <span>Business Category Name</span>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search category..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                    />
                  </div>
                </th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.length > 0 ? (
                filteredCategories.map((cat, index) => (
                  <tr key={cat.id}>
                    <td>{index + 1}</td>
                    <td
                      onClick={() => handleOpenEditModal(cat)}
                      style={{ cursor: "pointer" }}
                    >
                      {cat.name}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <FaEdit
                        className="action-icon text-primary"
                        onClick={() => handleOpenEditModal(cat)}
                        title="Edit"
                        style={{ cursor: "pointer", margin: "0 6px" }}
                      />
                      <FaTrash
                        className="action-icon text-danger"
                        onClick={() => handleDeleteClick(cat)}
                        title="Delete"
                        style={{ cursor: "pointer", margin: "0 6px" }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No categories found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Add/Edit Category Modal */}
      <Modal show={showAddModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? "Edit Business Category" : "Add New Business Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ name: e.target.value })}
                placeholder="Enter category name"
                disabled={submitLoading}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={submitLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddOrEditCategory}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {editingCategory ? "Updating..." : "Creating..."}
              </>
            ) : editingCategory ? (
              "Update Category"
            ) : (
              "Add Category"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
